import { useEffect, useMemo, useState } from 'react';
import type { DocumentData } from 'firebase/firestore';
import { AlertTriangle, ClipboardList, Lock } from 'lucide-react';
import { AlertBanner } from '@/components/AlertBanner';
import { Card } from '@/components/ui/card';
import { Loader } from '@/components/ui/loader';
import { useT } from '@/lib/i18n';
import { getAuditQuestionnaire } from '@/lib/firestore';
import { parseDataCatalog, sourceLabel, type Tone } from '@/lib/catalog';
import { fleetHostNames, parseRemoteInventory } from '@/lib/fleet';
import {
  observedFrom, parseDeclared, reconcile,
  type Declared, type MatchState, type ReconTopicKey,
} from '@/lib/reconcile';
import type { AuditDoc } from '@/types/models';
import { DataTable, Section, ToneBadge } from './CatalogShared';

/** `data_catalog` et `09_remote_inventory` : les deux sources de la colonne « observé ». */
const CATALOG_KEY = 'data_catalog';
const FLEET_KEY = '09_remote_inventory';

const STATE_TONE: Record<MatchState, Tone> = {
  match: 'success',
  declared_only: 'warning',
  observed_only: 'info',
  not_measured: 'neutral',
};

const TOPIC_ORDER: ReconTopicKey[] = ['hosts', 'gpu', 'cloud', 'sources'];

/**
 * Questionnaire soumis rattaché à l'audit (CPL-03/04). Chargé seulement quand
 * l'onglet qui s'en sert est ouvert — un audit consulté pour autre chose ne
 * paie pas la lecture.
 */
export function useAuditQuestionnaire(audit: AuditDoc | null, enabled: boolean) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const orgId = audit?.orgId;
  const questionnaireId = audit?.questionnaireId ?? null;

  useEffect(() => {
    if (!enabled || !orgId) return;
    let cancelled = false;
    setLoading(true);
    getAuditQuestionnaire(orgId, questionnaireId)
      .then(q => { if (!cancelled) setData((q?.data as Record<string, unknown>) ?? null); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) { setLoading(false); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [enabled, orgId, questionnaireId]);

  const declared = useMemo(() => parseDeclared(data), [data]);
  return { declared, loading, loaded };
}

/**
 * Onglet « Déclaré vs observé » : ce que le client a déclaré dans le
 * questionnaire AI Readiness, en face de ce que la collecte a réellement
 * trouvé. Chaque écart est une ligne exploitable — une machine déclarée jamais
 * vue, une source de données trouvée que personne ne suivait.
 *
 * Un sujet que l'audit n'a PAS regardé (pas de catalogue 1.2, pas de collecte
 * distante) est marqué « non mesuré » : il ne compte jamais comme un écart.
 */
export function ReconciliationTab({ audit, modules = {}, declared, loading, loaded }: {
  audit: AuditDoc;
  modules?: Record<string, DocumentData>;
  declared: Declared | null;
  loading: boolean;
  loaded: boolean;
}) {
  const { t } = useT();

  const observed = useMemo(() => {
    const catalog = parseDataCatalog(modules[CATALOG_KEY]?.json);
    const fleet = parseRemoteInventory(modules[FLEET_KEY]?.json);
    return observedFrom({
      systemName: audit.systemName,
      gpuCount: audit.summary?.gpuCount ?? null,
      gpuModel: audit.summary?.gpuModel ?? null,
      catalogSources: catalog
        ? catalog.sources.map(s => ({
          label: sourceLabel(s),
          locator: s.locator,
          host: s.hostedBy ?? s.host,
        }))
        : null,
      fleetHosts: fleetHostNames(fleet),
    });
  }, [audit.systemName, audit.summary?.gpuCount, audit.summary?.gpuModel, modules]);

  const recon = useMemo(() => reconcile(declared, observed), [declared, observed]);

  if (loading) return <Loader label={t('common.loading')} />;
  if (!recon) {
    return (
      <AlertBanner variant="info" icon={<ClipboardList className="h-4 w-4" />} title={t('audit.recon.none.title')}>
        <div className="text-xs">{loaded ? t('audit.recon.none.desc') : t('common.loading')}</div>
      </AlertBanner>
    );
  }

  return (
    <div className="space-y-6">
      <EgressBanner declared={declared} />
      <Card className="text-sm text-ink-600 dark:text-ink-300">
        {recon.gaps > 0 ? t('audit.recon.gaps', { n: recon.gaps }) : t('audit.recon.no_gap')}
      </Card>
      {TOPIC_ORDER.map(key => {
        const topic = recon.topics.find(tp => tp.key === key);
        /* v8 ignore next -- TOPIC_ORDER et reconcile() listent les mêmes sujets */
        if (!topic) return null;
        return (
          <Section
            key={key}
            title={t(`audit.recon.topic.${key}`)}
            description={t(`audit.recon.topic.${key}.desc`)}
            aside={topic.measured
              ? <span>{t('audit.recon.gaps.count', { n: topic.gaps })}</span>
              : <ToneBadge tone="neutral">{t('audit.recon.state.not_measured')}</ToneBadge>}
          >
            <DataTable
              empty={t('audit.recon.empty')}
              headers={[t('audit.recon.col.declared'), t('audit.recon.col.observed'), t('audit.recon.col.state')]}
              rows={topic.rows.map(row => [
                <span className="break-all">
                  {row.declared ?? <span className="text-ink-500">{t('audit.recon.not_declared')}</span>}
                  {row.detail && <span className="block text-xs text-ink-500">{row.detail}</span>}
                </span>,
                <span className="break-all">
                  {row.observed ?? <span className="text-ink-500">{t('audit.recon.not_observed')}</span>}
                </span>,
                <ToneBadge tone={STATE_TONE[topic.measured ? row.state : 'not_measured']}>
                  {t(`audit.recon.state.${topic.measured ? row.state : 'not_measured'}`)}
                </ToneBadge>,
              ])}
            />
          </Section>
        );
      })}
    </div>
  );
}

/**
 * « Les données ne peuvent pas sortir » est la contrainte qui décide de tout le
 * reste : elle passe en TÊTE des préconisations, pas dans une annexe. Le texte
 * exact saisi par le client est toujours reproduit — le portail ne réécrit pas
 * ce qu'il n'a pas mesuré.
 */
export function EgressBanner({ declared }: { declared: Declared | null }) {
  const { t } = useT();
  if (!declared || declared.egress === 'yes' || declared.egress === 'unknown') return null;
  const blocked = declared.egress === 'no';
  return (
    <AlertBanner
      variant={blocked ? 'danger' : 'warning'}
      icon={blocked ? <Lock className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
      title={t(blocked ? 'audit.recon.egress.no.title' : 'audit.recon.egress.conditional.title')}
    >
      <div className="text-xs">
        {t(blocked ? 'audit.recon.egress.no.desc' : 'audit.recon.egress.conditional.desc')}
        {declared.egressRaw && (
          <span className="mt-1 block">
            {t('audit.recon.egress.declared', { text: declared.egressRaw })}
          </span>
        )}
        {declared.redactionRequired === true && (
          <span className="mt-1 block">{t('audit.recon.egress.redaction')}</span>
        )}
      </div>
    </AlertBanner>
  );
}
